import { RefObject, useEffect } from 'react';

const PAYMENT_METHODS = ['ach', 'cc'] as const;
const PRICE_PATTERN = /^\$[\d,]+(?:\.\d+)?/;
const PROMOTION_MONTHS_PATTERN = /(\d+)\s+months?/i;

type PaymentMethod = (typeof PAYMENT_METHODS)[number];

const toAmount = (text: string | null) => Number(text?.replace(/[^\d.]/g, '') ?? NaN);

const setText = (element: Element | null, text: string) => {
  if (element && element.textContent !== text) element.textContent = text;
};

// Summed over the per item "Est. sales tax" rows, which the stylesheet hides
const getSalesTax = (panel: HTMLElement, method: PaymentMethod) => {
  const taxes = Array.from(
    panel.querySelectorAll(`[data-gc-dg-element='gc-dg-${method}-item-sales-tax']`),
    (tax) => toAmount(tax.textContent),
  );
  if (!taxes.length || taxes.some(Number.isNaN)) return undefined;
  return `$${taxes.reduce((sum, tax) => sum + tax, 0).toFixed(2)}`;
};

// The promotion length comes from the item promotion line, e.g. "20% off for 6 months"
const getPaymentsLabel = (panel: HTMLElement, method: PaymentMethod, hasPromotion: boolean) => {
  if (!hasPromotion) return 'Regular monthly payments';
  const promotion = panel.querySelector(
    `[data-gc-dg-element='gc-dg-${method}-item-discount-banner']`,
  )?.textContent;
  const months = promotion?.match(PROMOTION_MONTHS_PATTERN)?.[1];
  return months ? `First ${months} monthly payments` : 'Monthly payments';
};

const rewriteTotalRow = (panel: HTMLElement, method: PaymentMethod) => {
  const totalRow = panel.querySelector(`[data-gc-dg-element='gc-dg-${method}-monthly-total-row']`);
  if (!totalRow) return;

  // The SDK shows the promotion footnote with the total only while a promotion applies
  const hasPromotion = Boolean(
    totalRow.parentElement?.querySelector(
      `[data-gc-dg-element='gc-dg-${method}-monthly-promotion-text']`,
    ),
  );
  const paymentsLabel = getPaymentsLabel(panel, method, hasPromotion);
  const salesTax = getSalesTax(panel, method);
  // The stylesheet keeps the line break
  setText(
    totalRow.querySelector('p:first-child'),
    salesTax ? `${paymentsLabel},\nincl. ${salesTax} tax` : paymentsLabel,
  );

  // "$4.65 per month (inc tax)" becomes "$4.65": the label now says what the amount covers
  const amount = totalRow.querySelector('p:last-child');
  const price = amount?.textContent?.trim().match(PRICE_PATTERN)?.[0];
  if (price) setText(amount, price);
};

/**
 * @description Rewrites the order summary total of each payment method: the label names the
 * payments and their sales tax, and the amount stands alone. Repeated on every SDK re-render.
 */
export const useSummaryTotalCopy = (panelRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const rewrite = () => PAYMENT_METHODS.forEach((method) => rewriteTotalRow(panel, method));

    // A rewritten total no longer changes, so the mutations it causes end here
    const observer = new MutationObserver(rewrite);
    observer.observe(panel, { childList: true, subtree: true, characterData: true });
    rewrite();

    return () => observer.disconnect();
  }, [panelRef]);
};
